
import { BillingPeriod } from '@/types/product';

/**
 * Check if a billing period represents a recurring payment
 * @param billingPeriod The billing period to check
 * @returns true if recurring (not one-time), false otherwise
 */
export const isRecurring = (billingPeriod: BillingPeriod): boolean => {
  return (
    billingPeriod !== 'one_time' &&
    billingPeriod !== undefined
  );
};

/**
 * Get the Stripe-compatible interval from our internal billing period
 * @param billingPeriod Internal billing period representation
 * @returns Stripe-compatible interval string or null if one-time
 */
export const getStripeInterval = (
  billingPeriod: BillingPeriod
): { interval: string; intervalCount: number } | null => {
  if (billingPeriod === 'one_time') {
    return null;
  }

  switch (billingPeriod) {
    case 'daily':
      return { interval: 'day', intervalCount: 1 };
    case 'weekly':
      return { interval: 'week', intervalCount: 1 };
    case 'monthly':
      return { interval: 'month', intervalCount: 1 };
    case 'quarterly':
      return { interval: 'month', intervalCount: 3 };
    case 'yearly':
      return { interval: 'year', intervalCount: 1 };
    default:
      console.warn(`Unknown billing period: ${billingPeriod}, defaulting to monthly`);
      return { interval: 'month', intervalCount: 1 };
  }
};

/**
 * Check if a billing period is valid for recurring payments
 * @param billingPeriod The billing period to validate
 * @returns true if valid for recurring, false otherwise
 */
export const isValidRecurringBillingPeriod = (billingPeriod: BillingPeriod): boolean => {
  return ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].includes(billingPeriod);
};

/**
 * Convert any string to a valid billing period
 * @param period String from database or input
 * @returns Validated BillingPeriod enum value
 */
export const toBillingPeriod = (period: string | null | undefined): BillingPeriod => {
  if (!period) return 'one_time';
  
  const normalizedPeriod = period.toLowerCase();
  
  switch (normalizedPeriod) {
    case 'daily':
    case 'day':
      return 'daily';
    case 'weekly':
    case 'week':
      return 'weekly';
    case 'monthly':
    case 'month':
      return 'monthly';
    case 'quarterly':
    case 'quarter':
      return 'quarterly';
    case 'yearly':
    case 'year':
    case 'annual':
    case 'annually':
      return 'yearly';
    case 'one_time':
    case 'onetime':
    case 'once':
      return 'one_time';
    default:
      console.warn(`Unknown billing period: ${period}, defaulting to one_time`);
      return 'one_time';
  }
};

/**
 * Get standardized BillingPeriod from any string
 * @param period String value from database or API
 * @returns Valid BillingPeriod value
 */
export const getBillingPeriod = (period: string | null | undefined): BillingPeriod => {
  return toBillingPeriod(period);
};
